import { useEffect, useMemo } from 'react';

import { renderToString } from 'react-dom/server';

import type { IGeoJSONRoute, IGeoRequest } from '@quick/go-api-types/geo/route';

import { useAppTranslation } from '@/app/i18n/client';
import MarkerImg from '@/assets/mapIcons/map-pin-t.png';
import {
  LeafletMap,
  type LeafletTotalType,
  type ReactLeafletTotalType,
} from '@/components/core';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import { useLeafletMapContext } from '../core/LeafletContainer';
import { useFormDataContext } from '../demandForm/FormDataContext';

export const ServiceRouteMap = ({
  points,
}: {
  points: { lat: number; lng: number }[];
}) => {
  const coordinates = useMemo(
    () => points.map(({ lat, lng }) => ({ latitude: lat, longitude: lng })),
    [points]
  );

  const { id } = useCountry();
  const { user } = useFormDataContext();

  const { data } = useFetch<IGeoJSONRoute>({
    url: '/geo/route',
    method: 'POST',
    data: {
      coordinates,
      country_id: user?.country_id ?? id,
      geometries: 'geojson',
    } as IGeoRequest,
  });

  const pointsCoordinates = useMemo(() => {
    if (data)
      return {
        ...data?.linestring,
        //reverse coordinates because leaftlet uses Lat,Lng
        coordinates: reverseCoordinates(data?.linestring.coordinates),
      };
    return undefined;
  }, [data]);

  return (
    <LeafletMap
      scrollWheelZoom="center"
      gestureHandling
      className="min-h-[max(20rem,50vh)] w-full"
    >
      <InnerServiceRouteMap
        points={points}
        pointsCoordinates={pointsCoordinates}
      />
    </LeafletMap>
  );
};

function InnerServiceRouteMap({
  points,
  pointsCoordinates,
}: {
  points: { lat: number; lng: number }[];
  pointsCoordinates:
    | {
        coordinates: [number, number][];
        type: string;
      }
    | undefined;
}) {
  const { RL, L } = useLeafletMapContext();
  const { Marker, Polyline } = RL;
  const { DivIcon } = L;

  const allPoints = useMemo(() => {
    const newPoints =
      pointsCoordinates?.coordinates.map(([lat, lng]) => ({
        lat,
        lng,
      })) ?? [];
    return [...points, ...newPoints];
  }, [points, pointsCoordinates]);
  return (
    <>
      {points.map(({ lat, lng }, i) => (
        <PointMarker
          lat={lat}
          lng={lng}
          Marker={Marker}
          DivIcon={DivIcon}
          i={i}
          key={`${lat}-${lng} ${i}`}
        />
      ))}

      <Polyline
        pathOptions={{ color: '#FDD402' }}
        positions={pointsCoordinates?.coordinates || []}
        className="invert saturate-0 dark:invert-0 dark:saturate-100"
      />
      <Centerer points={allPoints} RL={RL} L={L} />
    </>
  );
}

type Coordinates = (number | Coordinates)[];

function reverseCoordinates<T extends Coordinates>(coordinates: T): T {
  if (coordinates.every((coord) => typeof coord === 'number')) {
    return [...coordinates].reverse() as T;
  }
  return coordinates.map((coordinate) => {
    if (Array.isArray(coordinate)) {
      return reverseCoordinates(coordinate);
    }
    return coordinate;
  }) as T;
}

function PointMarker({
  lat,
  lng,
  i,
  DivIcon,
  Marker,
}: {
  lat: number;
  lng: number;
  i: number;
  DivIcon: LeafletTotalType['DivIcon'];
  Marker: ReactLeafletTotalType['Marker'];
}) {
  const { t } = useAppTranslation(['common']);
  const alt = `${t('common:stop')} ${i + 1}`;
  const [width, height] = [MarkerImg.width / 1.5, MarkerImg.height / 1.5];
  const stopIcon = new DivIcon({
    html: renderToString(
      <div className="relative">
        <p
          className="absolute bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2 font-semibold
            text-black"
        >
          {i + 1}
        </p>
        <img
          src={MarkerImg.src}
          className=""
          alt={alt}
          style={{
            height: `${height}px`,
            width: `${width}px`,
          }}
        />
      </div>
    ),
    className: 'bg-transparent',
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
  });
  return <Marker position={[lat, lng]} icon={stopIcon} title={alt} />;
}

function Centerer({
  RL,
  points,
  L,
}: {
  RL: ReactLeafletTotalType;
  L: LeafletTotalType;
  points: {
    lat: number;
    lng: number;
  }[];
}) {
  const map = RL.useMap();
  const bounds = useMemo(() => {
    const first = points.at(0) ?? { lat: 0, lng: 0 };
    const b = new L.LatLngBounds(first, first);
    points.forEach((point) => b.extend(point));
    return b;
  }, [points, L.LatLngBounds]);
  useEffect(() => {
    map.fitBounds(bounds, { padding: [5, 5] });
  }, [bounds, map]);
  return null;
}
